import km from '@/utils/pm2Io'
import { FRONTEND_APP_TYPE } from '@/config/appTypes'

export const FETCH_APPS = 'FETCH_APPS'
export const GET_APP = 'GET_APP'
export const CREATE_APP = 'CREATE_APP'
export const UPDATE_APP = 'UPDATE_APP'
export const DELETE_APP = 'DELETE_APP'
const SET_APPS = 'SET_APPS'

function appModelToListModel(app) {
  return {
    id: app._id,
    name: app.name,
    domains: app.domains,
  }
}

function hasWebchecks(getters) {
  return getters.hasFeature({ feature: 'webchecks' })
}

function fetchWebchecks(bucketId, application) {
  return km.bucket.webcheck.list(bucketId, application).then(res => res.data)
}

function createWebcheck(bucketId, application, domain) {
  return km.bucket.webcheck
    .create(bucketId, {
      alerts: [],
      bucket: bucketId,
      enabled: true,
      interval: 60000,
      name: domain,
      retry: {
        interval: 5000,
        max: 0,
      },
      source: {
        region: 'eu-west-1',
      },
      target: {
        address: domain,
        is_frontend: true,
        // TODO remove port + type
        port: 443,
        type: 'https',
      },
      application,
      timeout: 2000,
    })
    .then(() => {})
}

function deleteWebcheck(bucketId, webcheckId) {
  return km.bucket.webcheck.delete(bucketId, webcheckId).then(() => {})
}

async function updateWebchecksForApp(bucketId, id, domains) {
  const webchecks = await fetchWebchecks(bucketId, id)
  const promises = []

  for (const domain of domains) {
    if (!webchecks.some(webcheck => webcheck.target.address === domain)) {
      promises.push(createWebcheck(bucketId, id, domain))
    }
  }

  for (const webcheck of webchecks) {
    if (!domains.includes(webcheck.target.address)) {
      promises.push(deleteWebcheck(bucketId, webcheck.id))
    }
  }

  return Promise.all(promises)
}

async function deleteWebchecksForApp(bucketId, id) {
  const webchecks = await fetchWebchecks(bucketId, id)
  return Promise.all(webchecks.map(webcheck => deleteWebcheck(bucketId, webcheck.id)))
}

export default {
  state: {
    apps: [],
  },
  mutations: {
    [SET_APPS]: (state, { apps }) => {
      state.apps = apps
    },
  },
  actions: {
    [FETCH_APPS]({ state, commit }, { bucketId }) {
      return km.bucket.application.list(bucketId).then(({ data: apps }) => {
        commit(SET_APPS, {
          apps: apps
            .map(app => {
              const browserApp = app.list.find(app => app.apm === 'browser')
              if (!browserApp) return
              return {
                name: app.name,
                id: browserApp.id,
                domains: browserApp.domains,
              }
            })
            .filter(Boolean),
        })
      })
    },

    [GET_APP]({ getters }, { bucketId = getters.currentBucket.bucket.id, id }) {
      return km.bucket.application.get(bucketId, id).then(res => res.data)
    },

    [CREATE_APP](
      { getters, commit, state },
      { bucketId = getters.currentBucket.bucket.id, name, domains, webchecks },
    ) {
      return km.bucket.application
        .create(bucketId, {
          name,
          domains,
        })
        .then(res => {
          const app = appModelToListModel(res.data)
          commit(SET_APPS, {
            apps: state.apps.concat(app),
          })

          if (hasWebchecks(getters) && webchecks) {
            return updateWebchecksForApp(bucketId, app.id, app.domains).then(() => app)
          }

          return app
        })
    },

    [UPDATE_APP](
      { getters, commit, state },
      { bucketId = getters.currentBucket.bucket.id, id, name, domains, webchecks },
    ) {
      let webchecksPromise
      if (hasWebchecks(getters)) {
        if (webchecks) {
          webchecksPromise = updateWebchecksForApp(bucketId, id, domains)
        } else {
          webchecksPromise = deleteWebchecksForApp(bucketId, id)
        }
      }

      return Promise.all([
        km.bucket.application.update(bucketId, id, {
          name,
          domains,
        }),
        webchecksPromise,
      ]).then(([res]) => {
        const app = appModelToListModel(res.data)
        commit(SET_APPS, {
          apps: state.apps.map(other => (other.id === app.id ? app : other)),
        })
        return app
      })
    },

    [DELETE_APP]({ getters, commit, state }, { bucketId = getters.currentBucket.bucket.id, id }) {
      let webchecksPromise
      if (hasWebchecks(getters)) {
        webchecksPromise = deleteWebchecksForApp(bucketId, id)
      }

      return Promise.all([km.bucket.application.delete(bucketId, id), webchecksPromise]).then(
        () => {
          commit(SET_APPS, {
            apps: state.apps.filter(other => other.id !== id),
          })
        },
      )
    },
  },
  getters: {
    frontendApps: state => state.apps,
    currentFrontendApp: (state, getters) =>
      getters.currentAppType === FRONTEND_APP_TYPE
        ? state.apps.find(app => app.name === getters.currentAppName)
        : undefined,
  },
}
